import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import {
  DollarSign, TrendingUp, TrendingDown, Clock, AlertTriangle,
  Ban, Target, Zap, ChevronRight,
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

export default function RevenueDashboard({ onChaseUnpaid, onFillGaps, showAdvanced = true }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetch = useCallback(async () => {
    try {
      const { data: d } = await axios.get(`${API}/api/income/revenue-dashboard`, { withCredentials: true });
      setData(d);
    } catch {}
    setLoading(false);
  }, []);

  useEffect(() => { fetch(); }, [fetch]);

  if (loading || !data) return null;

  const k = data.kpis;
  const weekChange = k.last_week_total > 0
    ? Math.round(((k.week.total - k.last_week_total) / k.last_week_total) * 100)
    : k.week.total > 0 ? 100 : 0;

  return (
    <div data-testid="revenue-dashboard" className="space-y-4">
      {/* ── Hero Revenue Card ── */}
      <div data-testid="revenue-hero" className="bg-gradient-to-br from-slate-900 to-slate-800 dark:from-slate-800 dark:to-slate-900 rounded-2xl p-5 text-white">
        <div className="flex items-start justify-between mb-4">
          <div>
            <p className="text-xs font-bold text-slate-400 uppercase tracking-wider">This Week</p>
            <p className="text-4xl font-extrabold tracking-tight mt-1">
              £{k.week.total.toFixed(0)}
            </p>
            {weekChange !== 0 && (
              <div className={`flex items-center gap-1 mt-1 ${weekChange > 0 ? "text-emerald-400" : "text-red-400"}`}>
                {weekChange > 0 ? <TrendingUp className="w-3.5 h-3.5" /> : <TrendingDown className="w-3.5 h-3.5" />}
                <span className="text-xs font-bold">{weekChange > 0 ? "+" : ""}{weekChange}% vs last week</span>
              </div>
            )}
          </div>
          <div className="text-right">
            <p className="text-xs text-slate-400 font-bold">Today</p>
            <p className="text-2xl font-extrabold">£{k.today.total.toFixed(0)}</p>
          </div>
        </div>

        {/* Weekly sparkline */}
        <div className="flex items-end gap-1.5" style={{ height: 44 }}>
          {data.daily_totals.map((d, i) => {
            const max = Math.max(...data.daily_totals.map(x => x.total), 1);
            const h = d.total > 0 ? Math.max(Math.round((d.total / max) * 36), 4) : 3;
            const isToday = d.date === new Date().toISOString().split("T")[0];
            return (
              <div key={i} className="flex-1 flex flex-col items-center gap-1">
                <div className={`w-full rounded-md ${isToday ? "bg-blue-400" : d.total > 0 ? "bg-emerald-400" : "bg-slate-700"}`} style={{ height: h }} />
                <span className={`text-[9px] font-bold ${isToday ? "text-blue-300" : "text-slate-500"}`}>{d.day}</span>
              </div>
            );
          })}
        </div>
      </div>

      {/* ── KPI Grid ── */}
      <div className="grid grid-cols-2 gap-3">
        <KPITile
          testId="kpi-month"
          label="This Month"
          value={`£${k.month.total.toFixed(0)}`}
          sub={`${k.month.count} lessons`}
          icon={DollarSign}
          color="text-emerald-600"
          bg="bg-emerald-50 dark:bg-emerald-900/15"
        />
        <KPITile
          testId="kpi-unpaid"
          label="Unpaid"
          value={`£${k.unpaid.total.toFixed(0)}`}
          sub={`${k.unpaid.count} lesson${k.unpaid.count !== 1 ? "s" : ""}`}
          icon={AlertTriangle}
          color="text-pink-600"
          bg="bg-pink-50 dark:bg-pink-900/15"
          alert={k.unpaid.total > 0}
        />
        <KPITile
          testId="kpi-cancelled"
          label="Cancelled Lost"
          value={`£${k.cancelled.total.toFixed(0)}`}
          sub={`${k.cancelled.count} this month`}
          icon={Ban}
          color="text-red-500"
          bg="bg-red-50 dark:bg-red-900/10"
        />
        <KPITile
          testId="kpi-avg"
          label="Avg Lesson"
          value={`£${k.avg_lesson_value.toFixed(0)}`}
          sub="per lesson"
          icon={Target}
          color="text-blue-600"
          bg="bg-blue-50 dark:bg-blue-900/15"
        />
        <KPITile
          testId="kpi-hours"
          label="Hours Booked"
          value={`${k.hours_booked}h`}
          sub={`${k.lessons_booked} lessons`}
          icon={Clock}
          color="text-violet-600"
          bg="bg-violet-50 dark:bg-violet-900/15"
        />
        <KPITile
          testId="kpi-empty"
          label="Empty Hours"
          value={`${k.empty_hours}h`}
          sub="available this week"
          icon={Zap}
          color="text-amber-600"
          bg="bg-amber-50 dark:bg-amber-900/15"
          alert={k.empty_hours > 15}
        />
      </div>

      {/* ── Weekly Trend ── (advanced — forecasting / pattern view).
          Feb 6, 2026: gated to `showAdvanced` so Basic users see only
          "understand money", not "optimise money". The caller in
          /instructor/money sets this from useInstructorTier(). */}
      {showAdvanced && data.weekly_trend?.length > 0 && (
        <div data-testid="weekly-trend" className="bg-white dark:bg-slate-800 rounded-2xl border border-slate-200 dark:border-slate-700 p-4">
          <p className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-3">8-Week Trend</p>
          <div className="flex items-end gap-2" style={{ height: 64 }}>
            {data.weekly_trend.map((w, i) => {
              const max = Math.max(...data.weekly_trend.map(x => x.total), 1);
              const h = w.total > 0 ? Math.max(Math.round((w.total / max) * 56), 4) : 3;
              const isThisWeek = i === data.weekly_trend.length - 1;
              return (
                <div key={i} className="flex-1 flex flex-col items-center gap-1">
                  <div className={`w-full rounded-md ${isThisWeek ? "bg-blue-500" : w.total > 0 ? "bg-slate-300 dark:bg-slate-600" : "bg-slate-100 dark:bg-slate-700"}`} style={{ height: h }} />
                  <span className="text-[8px] font-bold text-slate-400 leading-none">{w.label}</span>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* ── Best Earning Days ── (advanced — pattern recognition for
          schedule optimisation). Same gating rule as 8-week trend. */}
      {showAdvanced && data.day_averages?.length > 0 && (
        <div data-testid="best-days" className="bg-white dark:bg-slate-800 rounded-2xl border border-slate-200 dark:border-slate-700 p-4">
          <p className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-3">Best Days (Last 4 Weeks)</p>
          <div className="flex items-end gap-2" style={{ height: 48 }}>
            {["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"].map(day => {
              const found = data.day_averages.find(d => d.day === day);
              const total = found ? found.total : 0;
              const max = Math.max(...data.day_averages.map(x => x.total), 1);
              const h = total > 0 ? Math.max(Math.round((total / max) * 40), 4) : 3;
              return (
                <div key={day} className="flex-1 flex flex-col items-center gap-1">
                  <div className={`w-full rounded-md ${total > 0 ? "bg-emerald-400 dark:bg-emerald-500" : "bg-slate-100 dark:bg-slate-700"}`} style={{ height: h }} />
                  <span className="text-[9px] font-bold text-slate-400">{day}</span>
                  {total > 0 && <span className="text-[8px] font-bold text-emerald-600">£{total.toFixed(0)}</span>}
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

function KPITile({ testId, label, value, sub, icon: Icon, color, bg, alert }) {
  return (
    <div data-testid={testId} className={`rounded-2xl border border-slate-200 dark:border-slate-700 p-3.5 ${bg}`}>
      <div className="flex items-center gap-2 mb-1.5">
        <Icon className={`w-4 h-4 ${color}`} />
        <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">{label}</span>
      </div>
      <p className={`text-xl font-extrabold ${alert ? color : "text-slate-900 dark:text-white"}`}>{value}</p>
      <p className="text-[10px] text-slate-400 font-medium mt-0.5">{sub}</p>
    </div>
  );
}
